import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { LogIn, Menu, MessageCircle, X } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useSite } from "@/context/SiteContext";
import { LogoMark, Wordmark } from "@/components/Logo";
import { scrollToId } from "@/utils";
import { LOGIN_URL } from "@/config";

export const Header = () => {
  const { t, lang, toggle, isAr } = useLang();
  const { waLink } = useSite();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const loginLabel = isAr ? "الدخول" : "Login";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    ["features", t.nav.features],
    ["how", t.nav.how],
    ["hardware", t.nav.hardware],
    ["ai", t.nav.ai],
    ["faq", t.nav.faq],
  ];

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  const toTop = () => {
    setOpen(false);
    if (window.__lenis) window.__lenis.scrollTo(0);
    else window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled || open ? "border-b border-line/70 bg-cream/85 backdrop-blur-xl" : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-5 sm:px-8">
        <Link to="/" data-testid="nav-brand-logo" className="flex items-center gap-2.5" onClick={(e) => { e.preventDefault(); toTop(); }}>
          <LogoMark className="h-9 w-9" />
          <Wordmark />
        </Link>

        <nav className="hidden items-center gap-7 lg:flex">
          {links.map(([id, label]) => (
            <button
              key={id}
              onClick={() => go(id)}
              className="text-sm font-semibold text-inksoft transition-colors hover:text-pine"
            >
              {label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            data-testid="nav-login-link"
            href={LOGIN_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden items-center gap-2 rounded-full border border-line bg-white px-4 py-2 text-sm font-bold text-ink transition-all hover:border-pine hover:text-pine sm:flex"
          >
            <LogIn className="h-4 w-4" />
            {loginLabel}
          </a>
          <button
            data-testid="language-toggle-button"
            onClick={toggle}
            className="rounded-full border border-line bg-white px-4 py-2 text-sm font-bold text-ink transition-all hover:border-pine hover:text-pine"
            aria-label="Switch language"
          >
            {isAr ? "EN" : "ع"}
          </button>
          <a
            data-testid="header-whatsapp-button"
            href={waLink(lang)}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden items-center gap-2 rounded-full bg-terra px-5 py-2.5 text-sm font-bold text-white transition-all hover:bg-terra-dark sm:flex"
          >
            <MessageCircle className="h-4 w-4" />
            {t.nav.whatsapp}
          </a>
          <button
            data-testid="nav-request-quote-button"
            onClick={() => go("quote")}
            className="hidden rounded-full bg-pine px-5 py-2.5 text-sm font-bold text-cream transition-all hover:bg-pine-dark md:block"
          >
            {t.nav.quote}
          </button>
          <button className="grid h-10 w-10 place-items-center rounded-full border border-line bg-white lg:hidden" onClick={() => setOpen(!open)} aria-label={t.nav.menu} data-testid="mobile-menu-button">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden border-t border-line/70 bg-cream/95 backdrop-blur-xl lg:hidden"
          >
            <div className="space-y-1 px-5 py-4">
              <a href={LOGIN_URL} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 rounded-xl border border-line bg-white px-4 py-3 font-bold text-ink" data-testid="mobile-login-link">
                <LogIn className="h-4 w-4" /> {loginLabel}
              </a>
              {links.map(([id, label]) => (
                <button key={id} onClick={() => go(id)} className="block w-full rounded-xl px-4 py-3 text-start font-semibold text-ink hover:bg-pine-light">
                  {label}
                </button>
              ))}
              <a href={waLink(lang)} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 rounded-xl bg-terra px-4 py-3 font-bold text-white">
                <MessageCircle className="h-4 w-4" /> {t.nav.whatsapp}
              </a>
              <button onClick={() => go("quote")} className="w-full rounded-xl bg-pine px-4 py-3 font-bold text-cream">
                {t.nav.quote}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
